import React, { createContext, useContext, useState, useEffect } from "react";
import {
  AUTH_SESSION_EXPIRED_EVENT,
  COGNITO_TOKENS_STORAGE_KEY,
  isAuthenticated as checkAuth,
  signOut as cognitoSignOut,
  getUserInfo,
} from "../Access/services/authService";
import { useAuthTokenRefresh } from "../Access/hooks/useAuthTokenRefresh";
import { toast } from "../Components/Toast";
import { clearPostLoginRedirect } from "../Utils/postLoginRedirect";

export const SESSION_EXPIRED_TOAST_MESSAGE = 'Tu sesión venció. Iniciá sesión de nuevo.';

const AuthContext = createContext();

/**
 * La sesión vive en localStorage y `isAuthenticated` es síncrono. Si el primer
 * render arrancara en deslogueado, el header mostraría "Iniciar sesión" hasta
 * el useEffect aunque la sesión ya esté guardada.
 */
function readStoredSession() {
  try {
    if (!checkAuth()) return { isLoggedIn: false, user: null };
    return { isLoggedIn: true, user: getUserInfo() };
  } catch (error) {
    console.error('Error checking auth status:', error);
    return { isLoggedIn: false, user: null };
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [storedSession] = useState(readStoredSession);
  const [isLoggedIn, setIsLoggedIn] = useState(storedSession.isLoggedIn);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(storedSession.user);

  // Inicializar auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const authenticated = checkAuth();
        setIsLoggedIn(authenticated);
        
        // Si está autenticado, obtener info del usuario
        if (authenticated) {
          const userInfo = getUserInfo();
          setUser(userInfo);
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        setIsLoggedIn(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // Renovación proactiva del id token (docs/auth/cognito-session.md).
  useAuthTokenRefresh(isLoggedIn);

  useEffect(() => {
    // Refresh token muerto: authService ya hizo signOut. Sin redirect forzado:
    // el mapa y los shares públicos siguen andando deslogueados.
    const onSessionExpired = () => {
      setIsLoggedIn(false);
      setUser(null);
      toast.warning(SESSION_EXPIRED_TOAST_MESSAGE);
    };
    // Logout en otra pestaña: los tokens desaparecen del storage compartido.
    const onStorage = (event) => {
      if (event.key !== null && event.key !== COGNITO_TOKENS_STORAGE_KEY) return;
      if (checkAuth()) return;
      setIsLoggedIn(false);
      setUser(null);
    };
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const login = async (userData) => {
    // userData viene de signIn() en authService
    setIsLoggedIn(true);
    setUser(userData);
    try {
      sessionStorage.setItem('terrio_show_feedback_hint', '1');
    } catch (_) {
      // sessionStorage puede no estar disponiblee
    }
  };

  const logout = async () => {
    try {
      cognitoSignOut();
      setIsLoggedIn(false);
      setUser(null);
      clearPostLoginRedirect();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    isLoggedIn,
    isLoading,
    user,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};